import React, { useState } from "react";
import {
  Card,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@material-ui/core";
import { PrimaryText } from "components/Common/PrimaryText";
import InfoTooltip from "./InfoTooltip";
import { useAtRiskUsers } from "hooks/useRetentionData";
import { exportAtRiskUsers } from "services/retention-service";
import { sendCustomPushNotification } from "services/notification-service";
import { useDisplay } from "context/DisplayContext";

// Simple avatar component with initials
const Avatar = ({ name, size = 40 }) => {
  const getInitials = (name) => {
    if (!name) return "?";
    // Handle email format - get first letter of username part
    if (name.includes("@")) {
      const username = name.split("@")[0];
      const parts = username.split(".");
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return username.substring(0, 2).toUpperCase();
    }
    // Handle regular name format
    const names = name.split(" ");
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getAvatarColor = (name) => {
    if (!name) return "#9e9e9e";
    const colors = [
      "#f44336",
      "#e91e63",
      "#9c27b0",
      "#673ab7",
      "#3f51b5",
      "#2196f3",
      "#03a9f4",
      "#00bcd4",
      "#009688",
      "#4caf50",
      "#8bc34a",
      "#cddc39",
      "#ffeb3b",
      "#ffc107",
      "#ff9800",
      "#ff5722",
      "#795548",
      "#9e9e9e",
      "#607d8b",
    ];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
  };

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: getAvatarColor(name),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "white",
        fontWeight: 500,
        fontSize: size * 0.4,
      }}
    >
      {getInitials(name)}
    </div>
  );
};

const AtRiskUsersList = ({ filter }) => {
  const { isMobile } = useDisplay();
  const [riskType, setRiskType] = useState("");
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationTitle, setNotificationTitle] = useState("");
  const [sendingNotification, setSendingNotification] = useState(false);
  // Remove limit to show all at-risk users (use a large number instead of null)
  const { users, loading, error, refetch } = useAtRiskUsers(
    riskType,
    250, // Large limit to show all users
    filter
  );

  if (loading) {
    return (
      <Card style={{ padding: 20, textAlign: "center", height: isMobile ? "auto" : 400 }}>
        <CircularProgress />
        <PrimaryText>Loading at-risk users...</PrimaryText>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ padding: 20, textAlign: "center", height: isMobile ? "auto" : 400 }}>
        <PrimaryText color="error">
          Error loading at-risk users: {error}
        </PrimaryText>
        <Button
          onClick={refetch}
          variant="contained"
          color="primary"
          style={{ marginTop: 16 }}
        >
          Retry
        </Button>
      </Card>
    );
  }

  const handleExport = () => {
    exportAtRiskUsers(riskType || null, filter);
  };

  const reachableUsers = users.filter(
    (u) => u.hasTokens && u.pushNotificationsEnabled
  );
  const reachableCount = reachableUsers.length;

  const handleSendNotification = async () => {
    if (!notificationTitle.trim()) {
      alert("Please enter a notification title");
      return;
    }

    if (!notificationMessage.trim()) {
      alert("Please enter a notification message");
      return;
    }

    const userIds = users
      .filter((user) => user.hasTokens && user.pushNotificationsEnabled)
      .map((user) => user.userId);

    if (userIds.length === 0) {
      alert("No users with push notification tokens to send to.");
      return;
    }

    setSendingNotification(true);
    try {
      // Send notification to the specified users
      const result = await sendCustomPushNotification(
        notificationTitle,
        notificationMessage,
        userIds
      );

      // Close modal and reset form
      setNotificationModalOpen(false);
      setNotificationMessage("");
      setNotificationTitle("");

      const summary = result.message || "Notifications sent successfully!";
      if (result.sentCount === 0) {
        alert(`No notifications were delivered. ${summary}`);
      } else {
        alert(summary);
      }
    } catch (error) {
      console.error("Error sending notifications:", error);
      alert("Error sending notifications. Please try again.");
    } finally {
      setSendingNotification(false);
    }
  };

  return (
    <Card
      style={{
        padding: isMobile ? 12 : 20,
        height: isMobile ? "auto" : 400,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
        <PrimaryText fontSize={isMobile ? 16 : 20} fontWeight={700}>
          At-Risk Users
        </PrimaryText>
        <InfoTooltip
          title="Take action"
          content="Identified users who may churn soon. Consider targeted outreach campaigns."
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: isMobile ? "flex-start" : "center",
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 8 : 0,
          marginBottom: 16,
        }}
      >
        <FormControl
          variant="outlined"
          size="small"
          style={{ minWidth: 120, width: isMobile ? "100%" : undefined }}
        >
          <InputLabel>Risk Type</InputLabel>
          <Select
            value={riskType}
            onChange={(e) => setRiskType(e.target.value)}
            label="Risk Type"
          >
            <MenuItem value="">All Types</MenuItem>
            <MenuItem value="never_active">Never Active</MenuItem>
            <MenuItem value="inactive_7_days">Inactive 7 Days</MenuItem>
            <MenuItem value="inactive_14_days">Inactive 14 Days</MenuItem>
            <MenuItem value="inactive_30_days">Inactive 30+ Days</MenuItem>
          </Select>
        </FormControl>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: isMobile ? "flex-start" : "flex-end",
            width: isMobile ? "100%" : undefined,
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 8,
              width: isMobile ? "100%" : undefined,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleExport}
              disabled={users.length === 0}
              style={{ flex: isMobile ? 1 : undefined }}
            >
              Export CSV
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setNotificationModalOpen(true)}
              disabled={reachableCount === 0}
              style={{ flex: isMobile ? 1 : undefined }}
            >
              Engage
            </Button>
          </div>
          {reachableCount === 0 && users.length > 0 && (
            <PrimaryText fontSize={isMobile ? 11 : 12} style={{ color: "#999", marginTop: 4 }}>
              No users with push notification tokens in this list
            </PrimaryText>
          )}
        </div>
      </div>

      <div style={{ overflowY: "auto", flex: 1, width: "100%", overflowX: "auto" }}>
        {users.length === 0 ? (
          <div style={{ textAlign: "center", padding: 40 }}>
            <PrimaryText>No at-risk users found</PrimaryText>
            <PrimaryText fontSize={14} style={{ color: "#666", marginTop: 8 }}>
              All users are currently engaged
            </PrimaryText>
          </div>
        ) : (
          <>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: isMobile ? 400 : undefined }}>
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: "left",
                      padding: isMobile ? "6px 4px" : "8px",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600}>
                      User
                    </PrimaryText>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: isMobile ? "6px 4px" : "8px",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600}>
                      Risk Type
                    </PrimaryText>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: isMobile ? "6px 4px" : "8px",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600}>
                      Last Login
                    </PrimaryText>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: isMobile ? "6px 4px" : "8px",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600}>
                      Last Engaged
                    </PrimaryText>
                  </th>
                  <th
                    style={{
                      textAlign: "center",
                      padding: isMobile ? "6px 4px" : "8px",
                      borderBottom: "1px solid #ddd",
                    }}
                  >
                    <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={600}>
                      Notifications
                    </PrimaryText>
                  </th>
                </tr>
              </thead>
              <tbody>
                {users.map((user, index) => (
                  <tr key={user.userId || index}>
                    <td
                      style={{ padding: isMobile ? "6px 4px" : "8px", borderBottom: "1px solid #ddd" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          gap: 12,
                        }}
                      >
                        {user.photoURL ? (
                          <img
                            src={user.photoURL}
                            alt="Profile"
                            style={{
                              width: isMobile ? 32 : 40,
                              height: isMobile ? 32 : 40,
                              borderRadius: "50%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <Avatar
                            name={user.displayName || user.email}
                            size={isMobile ? 32 : 40}
                          />
                        )}
                        <div
                          style={{ display: "flex", flexDirection: "column" }}
                        >
                          <PrimaryText fontSize={isMobile ? 12 : 14} fontWeight={500}>
                            {user.displayName || user.email}
                          </PrimaryText>
                          <PrimaryText fontSize={isMobile ? 11 : 12} style={{ color: "#666" }}>
                            {user.email}
                          </PrimaryText>
                          <PrimaryText fontSize={11} style={{ color: "#999" }}>
                            {user.riskReason}
                          </PrimaryText>
                        </div>
                      </div>
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        padding: isMobile ? "6px 4px" : "8px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor:
                            user.riskType === "inactive_30_days"
                              ? "#f44336"
                              : user.riskType === "inactive_14_days"
                              ? "#ff9800"
                              : user.riskType === "inactive_7_days"
                              ? "#ffeb3b"
                              : "#9e9e9e",
                          color: [
                            "inactive_30_days",
                            "inactive_14_days",
                          ].includes(user.riskType)
                            ? "white"
                            : "black",
                          borderRadius: "4px",
                          padding: isMobile ? "3px 4px" : "4px 8px",
                          display: "inline-block",
                          fontSize: isMobile ? 11 : 12,
                          fontWeight: 500,
                        }}
                      >
                        {user.riskType
                          ?.replace(/_/g, " ")
                          .replace(/\b\w/g, (l) => l.toUpperCase()) ||
                          "Unknown"}
                      </div>
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        padding: isMobile ? "6px 4px" : "8px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <PrimaryText fontSize={isMobile ? 12 : 14}>
                        {user.lastLogin &&
                        Object.keys(user.lastLogin).length > 0
                          ? new Date(
                              user.lastLogin._seconds
                                ? user.lastLogin._seconds * 1000
                                : user.lastLogin
                            ).toLocaleDateString()
                          : "Never"}
                      </PrimaryText>
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        padding: isMobile ? "6px 4px" : "8px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      <PrimaryText fontSize={isMobile ? 12 : 14}>
                        {user.lastEngagedAt
                          ? new Date(
                              user.lastEngagedAt._seconds
                                ? user.lastEngagedAt._seconds * 1000
                                : user.lastEngagedAt
                            ).toLocaleDateString()
                          : "Never"}
                      </PrimaryText>
                    </td>
                    <td
                      style={{
                        textAlign: "center",
                        padding: isMobile ? "6px 4px" : "8px",
                        borderBottom: "1px solid #ddd",
                      }}
                    >
                      {(() => {
                        const labelStyle = {
                          fontSize: isMobile ? 11 : 12,
                          color: "#666",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 4,
                          whiteSpace: "nowrap",
                        };

                        if (user.hasTokens === undefined) {
                          return (
                            <span style={labelStyle}>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: "#9e9e9e",
                                }}
                              />
                              Unknown
                            </span>
                          );
                        }

                        if (user.hasTokens && user.pushNotificationsEnabled) {
                          return (
                            <span style={labelStyle}>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: "#4caf50",
                                }}
                              />
                              Has token
                            </span>
                          );
                        }

                        if (!user.hasTokens) {
                          return (
                            <span style={labelStyle}>
                              <span
                                style={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: "#9e9e9e",
                                }}
                              />
                              No token
                            </span>
                          );
                        }

                        return (
                          <span style={labelStyle}>
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "#f44336",
                              }}
                            />
                            Opted out
                          </span>
                        );
                      })()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {/* Notification Modal */}
      <Dialog
        open={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Send Custom Notification</DialogTitle>
        <DialogContent>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            Send a push notification to {reachableCount} of {users.length} at-risk users
            who have device tokens.
            {reachableCount < users.length && (
              <span style={{ color: "#999", display: "block", marginTop: 4 }}>
                {users.length - reachableCount} users will be skipped (no device token or opted out).
              </span>
            )}
            <span style={{ color: "#999", display: "block", marginTop: 4 }}>
              Some tokens may be expired. You will see a delivery summary after sending.
            </span>
          </Typography>
          <TextField
            label="Notification Title"
            fullWidth
            variant="outlined"
            value={notificationTitle}
            onChange={(e) => setNotificationTitle(e.target.value)}
            helperText="Enter the title for your notification"
            style={{ marginBottom: 16 }}
          />
          <TextField
            label="Notification Message"
            multiline
            rows={4}
            fullWidth
            variant="outlined"
            value={notificationMessage}
            onChange={(e) => setNotificationMessage(e.target.value)}
            helperText="Enter the message you want to send to at-risk users"
            style={{ marginBottom: 16 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setNotificationModalOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSendNotification}
            color="primary"
            variant="contained"
            disabled={
              sendingNotification ||
              !notificationTitle.trim() ||
              !notificationMessage.trim()
            }
          >
            {sendingNotification ? "Sending..." : "Send Notification"}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default AtRiskUsersList;
